/**
 * Masters → Employees (PF / ESI / PT / TDS on salary).
 * @file src/web/features/admin/components/masters/EmployeesTab.tsx
 *
 * Phase 2. The master is editable now so data can be collected, but nothing
 * computes a deduction: the statutory rate tables are empty and the resolver
 * refuses rather than returning a default. The banner says so explicitly —
 * a screen that silently shows zero deductions reads as "nothing is owed".
 *
 * Per-employee gaps are shown inline rather than only in the Gaps tab, because
 * this is where they get fixed.
 */

import { useCallback, useEffect, useState } from 'react';
import { UserRound, Trash2, Pencil, AlertTriangle, IndianRupee, X, FileText } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import { adminFetchJson } from '../../lib/admin-api';
import {
  Card, Field, TextInput, Select, PrimaryBtn, GhostBtn, AddToggle, FormError,
  Loading, ErrBox, EmptyState, Th, Td, Pill, useEntityResource, mutate, useDeleteConfirm,
} from './shared';

interface SalaryRow {
  id: string; effectiveFrom: string;
  basic: string; da: string; hra: string; specialAllowance: string; otherAllowance: string; grossMonthly: string;
}
interface Employee {
  id: string; code: string | null; name: string;
  pan: string | null; aadhaarLast4: string | null; uan: string | null; esiIpNumber: string | null;
  dateOfBirth: string | null; dateOfJoining: string | null; dateOfLeaving: string | null;
  gender: string | null; workStateCode: string | null;
  employmentCategory: string | null; taxRegime: string | null; status: string;
  salary: SalaryRow[];
  gaps: string[];
}
interface Readiness {
  employees: number;
  workStates: { code: string; name: string | null }[];
  ratesReady: boolean;
  missingRates: { regime: string; what: string }[];
  canCompute: boolean;
}
interface Meta {
  employmentCategories: string[]; taxRegimes: string[];
}

const GAP_LABEL: Record<string, string> = {
  EMP_NO_PAN: 'PAN',
  EMP_NO_UAN: 'UAN',
  EMP_NO_ESI_IP: 'ESI IP',
  EMP_NO_WORK_STATE: 'work state',
  EMP_NO_DOJ: 'joining date',
  EMP_NO_REGIME: 'tax regime',
  EMP_NO_SALARY: 'salary structure',
  EMP_NO_DECLARATIONS: 'declarations',
};

export function EmployeesTab({ entityId }: { entityId: string | null }) {
  const { items, loading, error, reload } = useEntityResource<Employee>('employees', entityId);
  const { askDelete, deleteDialog } = useDeleteConfirm();
  const [meta, setMeta] = useState<Meta | null>(null);
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [salaryFor, setSalaryFor] = useState<Employee | null>(null);
  const [declFor, setDeclFor] = useState<Employee | null>(null);

  useEffect(() => { adminFetchJson<Meta>('/api/masters/meta').then(setMeta).catch(() => setMeta(null)); }, []);

  const loadReadiness = useCallback(() => {
    if (!entityId) return;
    adminFetchJson<Readiness>('/api/masters/employees/readiness', { query: { entityId } })
      .then(setReadiness).catch(() => setReadiness(null));
  }, [entityId]);
  useEffect(loadReadiness, [loadReadiness]);

  if (loading) return <Loading label="Loading employees" />;
  if (error) return <ErrBox msg={error} />;

  const refresh = () => { reload(); loadReadiness(); };

  return (
    <>
      {/* Say plainly that nothing is computed yet — a screen full of zeroes
          would otherwise read as "no deductions are due". */}
      {readiness && !readiness.canCompute && (
        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-warn-200 bg-warn-50 px-6 py-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn-600" />
          <div className="text-sm text-warn-900">
            <p className="font-semibold">Statutory computation is off</p>
            <p className="mt-1">
              Employee records can be maintained here, but no PF, ESI, PT or salary TDS is calculated —
              the rate tables have not been loaded, and computing on assumed rates would produce a
              confidently wrong deduction.
            </p>
            {readiness.missingRates.length > 0 && (
              <ul className="mt-2 space-y-0.5">
                {readiness.missingRates.map((m) => (
                  <li key={m.regime} className="text-[13px]">
                    <span className="font-semibold">{m.regime}</span> — {m.what}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {readiness && readiness.workStates.length > 1 && (
        <div className="mb-6 rounded-2xl border border-ink-200 bg-white px-6 py-4 text-sm text-ink-600">
          Staff are located in {readiness.workStates.length} states
          ({readiness.workStates.map((s) => s.name ?? s.code).join(', ')}) — Professional Tax is
          legislated per state, so a slab table is needed for each.
        </div>
      )}

      <Card
        title="Employees"
        subtitle="PF, ESI, Professional Tax and TDS on salary all read from this master. Work location decides which PT slab applies — the employer's state is the wrong answer for staff based elsewhere."
        action={<AddToggle open={adding} onToggle={() => { setAdding(!adding); setEditing(null); }} label="Add employee" />}
      >
        {(adding || editing) && (
          <EmployeeForm
            meta={meta}
            initial={editing ?? undefined}
            onCancel={() => { setAdding(false); setEditing(null); }}
            onSave={async (body) => {
              if (editing) await mutate(`employees/${editing.id}`, 'PATCH', { entityId, ...body });
              else await mutate('employees', 'POST', { entityId, ...body });
              setAdding(false); setEditing(null);
              toast.success(editing ? 'Employee updated' : 'Employee added');
              refresh();
            }}
          />
        )}

        {items.length === 0 && !adding ? (
          <EmptyState
            what="employee"
            why="Only needed for clients subject to PF, ESI or Professional Tax. The schema is in place so this can be populated ahead of the computation layer."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <Th>Employee</Th><Th>PAN</Th><Th>UAN</Th><Th>Work state</Th>
                  <Th>Regime</Th><Th right>Gross / month</Th><Th>Missing</Th><Th right>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {items.map((e) => (
                  <tr key={e.id} className="hover:bg-ink-50/50">
                    <Td>
                      <span className="inline-flex items-center gap-2">
                        <UserRound className="h-3.5 w-3.5 text-ink-400" />
                        <span>
                          {e.name}
                          {e.code && <span className="ml-1.5 text-xs text-ink-400">{e.code}</span>}
                        </span>
                      </span>
                    </Td>
                    <Td mono>{e.pan ?? <span className="text-warn-600">—</span>}</Td>
                    <Td mono>{e.uan ?? <span className="text-warn-600">—</span>}</Td>
                    <Td muted>{e.workStateCode ?? <span className="text-warn-600">not set</span>}</Td>
                    <Td>{e.taxRegime ? <Pill tone="neutral">{e.taxRegime}</Pill> : <span className="text-warn-600">—</span>}</Td>
                    <Td right mono>
                      {e.salary?.[0]
                        ? `₹${Number(e.salary[0].grossMonthly).toLocaleString('en-IN')}`
                        : <span className="text-warn-600">not set</span>}
                    </Td>
                    <Td>
                      {e.gaps.length === 0
                        ? <Pill tone="ok">complete</Pill>
                        : <span className="text-[11px] text-warn-700">{e.gaps.map((g) => GAP_LABEL[g] ?? g).join(', ')}</span>}
                    </Td>
                    <Td right>
                      <span className="inline-flex gap-1.5">
                        <GhostBtn onClick={() => setSalaryFor(e)}><IndianRupee className="h-3 w-3" /> Salary</GhostBtn>
                        {/* Chapter VI-A deductions are an old-regime feature; the
                            button is hidden rather than shown-and-refused. */}
                        {e.taxRegime === 'old' && (
                          <GhostBtn onClick={() => setDeclFor(e)}><FileText className="h-3 w-3" /> Declarations</GhostBtn>
                        )}
                        <GhostBtn onClick={() => { setEditing(e); setAdding(false); }}><Pencil className="h-3 w-3" /></GhostBtn>
                        <GhostBtn tone="danger" onClick={() => askDelete(
                          `${e.name} and their salary history`,
                          async () => {
                            try { await mutate(`employees/${e.id}`, 'DELETE', { entityId }); toast.success('Removed'); refresh(); }
                            catch (err) { toast.error((err as Error).message); }
                          },
                        )}><Trash2 className="h-3 w-3" /></GhostBtn>
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {salaryFor && (
        <SalaryPanel
          entityId={entityId}
          employee={salaryFor}
          onClose={() => { setSalaryFor(null); refresh(); }}
        />
      )}

      {declFor && (
        <DeclarationsPanel
          entityId={entityId}
          employee={declFor}
          onClose={() => { setDeclFor(null); refresh(); }}
        />
      )}
      {deleteDialog}
    </>
  );
}

function EmployeeForm({ initial, meta, onSave, onCancel }: {
  initial?: Employee; meta: Meta | null;
  onSave: (body: Record<string, unknown>) => Promise<void>; onCancel: () => void;
}) {
  const d = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : '');
  const [f, setF] = useState({
    name: initial?.name ?? '', code: initial?.code ?? '',
    pan: initial?.pan ?? '', aadhaarLast4: initial?.aadhaarLast4 ?? '',
    uan: initial?.uan ?? '', esiIpNumber: initial?.esiIpNumber ?? '',
    dateOfBirth: d(initial?.dateOfBirth), dateOfJoining: d(initial?.dateOfJoining), dateOfLeaving: d(initial?.dateOfLeaving),
    gender: initial?.gender ?? '', workStateCode: initial?.workStateCode ?? '',
    employmentCategory: initial?.employmentCategory ?? 'regular', taxRegime: initial?.taxRegime ?? 'new',
  });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true); setErr(null);
    try { await onSave(f); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="border-b border-ink-100 bg-ink-50/60 px-6 py-5">
      <FormError msg={err} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Name"><TextInput value={f.name} onChange={set('name')} /></Field>
        <Field label="Employee code"><TextInput value={f.code} onChange={set('code')} /></Field>
        <Field label="PAN"><TextInput value={f.pan} onChange={(v) => set('pan')(v.toUpperCase())} mono /></Field>
        <Field label="Aadhaar (last 4 only)" hint="The full number is deliberately never stored.">
          <TextInput value={f.aadhaarLast4} onChange={set('aadhaarLast4')} placeholder="1234" mono />
        </Field>
        <Field label="UAN" hint="12 digits, issued by EPFO."><TextInput value={f.uan} onChange={set('uan')} mono /></Field>
        <Field label="ESI IP number"><TextInput value={f.esiIpNumber} onChange={set('esiIpNumber')} mono /></Field>
        <Field label="Date of birth"><TextInput type="date" value={f.dateOfBirth} onChange={set('dateOfBirth')} /></Field>
        <Field label="Date of joining"><TextInput type="date" value={f.dateOfJoining} onChange={set('dateOfJoining')} /></Field>
        <Field label="Date of leaving"><TextInput type="date" value={f.dateOfLeaving} onChange={set('dateOfLeaving')} /></Field>
        <Field label="Work location (state)" hint="Decides the PT slab — not the employer's state.">
          <TextInput value={f.workStateCode} onChange={set('workStateCode')} placeholder="Karnataka or 29" />
        </Field>
        <Field label="Employment category" hint="Changes PF applicability, not just the rate.">
          <Select value={f.employmentCategory} onChange={set('employmentCategory')} options={meta?.employmentCategories ?? ['regular']} />
        </Field>
        <Field label="Tax regime" hint="Old regime allows Chapter VI-A deductions; new does not.">
          <Select value={f.taxRegime} onChange={set('taxRegime')} options={meta?.taxRegimes ?? ['old', 'new']} />
        </Field>
        <Field label="Gender"><Select value={f.gender} onChange={set('gender')} options={['female', 'male', 'other']} /></Field>
      </div>
      <div className="mt-5 flex items-center gap-3">
        <PrimaryBtn onClick={submit} busy={busy}>{initial ? 'Save changes' : 'Add employee'}</PrimaryBtn>
        <button onClick={onCancel} className="text-sm font-medium text-ink-500 hover:text-ink-700">Cancel</button>
      </div>
    </div>
  );
}

/**
 * Chapter VI-A investment declarations. Only reachable for an old-regime
 * employee — the new regime does not allow these deductions, and the API
 * refuses them, so the entry point is hidden rather than shown-and-rejected.
 *
 * Declared and proved are tracked separately on purpose: monthly TDS runs on
 * what the employee declared, the year-end true-up runs on what they actually
 * proved, and the difference is exactly what a payroll team chases in Q4.
 */
function DeclarationsPanel({ entityId, employee, onClose }: {
  entityId: string | null; employee: Employee; onClose: () => void;
}) {
  const { askDelete, deleteDialog } = useDeleteConfirm();
  interface Decl {
    id: string; financialYear: string; section: string;
    declaredAmount: string; provedAmount: string | null; proofStatus: string;
  }
  const [rows, setRows] = useState<Decl[]>([]);
  const [sections, setSections] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [f, setF] = useState({ financialYear: '', section: '', declaredAmount: '', provedAmount: '', proofStatus: 'declared' });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminFetchJson<{ declarationSections: string[]; proofStatuses: string[] }>('/api/masters/meta')
      .then((d) => { setSections(d.declarationSections ?? []); setStatuses(d.proofStatuses ?? []); })
      .catch(() => { /* dropdowns fall back to free entry */ });
  }, []);

  const load = useCallback(() => {
    if (!entityId) return;
    adminFetchJson<{ items: Decl[] }>(`/api/masters/employees/${employee.id}/declarations`, { query: { entityId } })
      .then((d) => setRows(d.items ?? [])).catch(() => setRows([]));
  }, [entityId, employee.id]);
  useEffect(load, [load]);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await mutate(`employees/${employee.id}/declarations`, 'POST', { entityId, ...f });
      setF({ financialYear: f.financialYear, section: '', declaredAmount: '', provedAmount: '', proofStatus: 'declared' });
      toast.success('Declaration saved'); load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const money = (v: string | null) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN')}`);
  const tone = (s: string): 'ok' | 'danger' | 'brand' | 'neutral' =>
    (s === 'verified' ? 'ok' : s === 'rejected' ? 'danger' : s === 'submitted' ? 'brand' : 'neutral');

  const declaredTotal = rows.reduce((s, r) => s + Number(r.declaredAmount), 0);
  const provedTotal = rows.reduce((s, r) => s + Number(r.provedAmount ?? 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/40 p-6">
      <div className="w-full max-w-4xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-ink-100 px-6 py-5">
          <div>
            <h3 className="text-base font-semibold text-ink-900">Investment declarations — {employee.name}</h3>
            <p className="mt-1 max-w-2xl text-sm text-ink-500">
              Monthly TDS runs on what was declared; the year-end true-up runs on what was proved.
              Tracking both is what makes the Q4 shortfall visible before it becomes a surprise.
            </p>
          </div>
          <button onClick={onClose} className="rounded-full p-1 text-ink-400 hover:bg-ink-100" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-ink-100 bg-ink-50/60 px-6 py-5">
          <FormError msg={err} />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Field label="Financial year"><TextInput value={f.financialYear} onChange={set('financialYear')} placeholder="2025-26" /></Field>
            <Field label="Section"><Select value={f.section} onChange={set('section')} options={sections} placeholder="Select" /></Field>
            <Field label="Declared"><TextInput value={f.declaredAmount} onChange={set('declaredAmount')} mono /></Field>
            <Field label="Proved" hint="Required before marking verified."><TextInput value={f.provedAmount} onChange={set('provedAmount')} mono /></Field>
            <Field label="Proof status"><Select value={f.proofStatus} onChange={set('proofStatus')} options={statuses} placeholder="declared" /></Field>
          </div>
          <div className="mt-5"><PrimaryBtn onClick={submit} busy={busy}>Save declaration</PrimaryBtn></div>
        </div>

        {rows.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-ink-500">
            No declarations recorded. Without them, monthly TDS is computed with no Chapter VI-A relief.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr><Th>FY</Th><Th>Section</Th><Th right>Declared</Th><Th right>Proved</Th><Th>Status</Th><Th right>Actions</Th></tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-ink-50/50">
                    <Td muted>{r.financialYear}</Td>
                    <Td>{r.section}</Td>
                    <Td right mono>{money(r.declaredAmount)}</Td>
                    <Td right mono>
                      {money(r.provedAmount)}
                      {r.provedAmount != null && Number(r.provedAmount) < Number(r.declaredAmount) && (
                        <span className="ml-1.5 text-[11px] text-warn-600">short</span>
                      )}
                    </Td>
                    <Td><Pill tone={tone(r.proofStatus)}>{r.proofStatus}</Pill></Td>
                    <Td right>
                      <GhostBtn tone="danger" onClick={() => askDelete(
                        `the ${r.section} declaration for ${r.financialYear}`,
                        async () => {
                          try {
                            await mutate(`employees/${employee.id}/declarations/${r.id}`, 'DELETE', { entityId });
                            toast.success('Removed'); load();
                          } catch (e) { toast.error((e as Error).message); }
                        },
                      )}><Trash2 className="h-3 w-3" /></GhostBtn>
                    </Td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t border-ink-200 bg-ink-50/60">
                <tr>
                  <Td muted>Total</Td><Td> </Td>
                  <Td right mono>{money(String(declaredTotal))}</Td>
                  <Td right mono>{money(String(provedTotal))}</Td>
                  <Td> </Td><Td> </Td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      {deleteDialog}
    </div>
  );
}

/**
 * Salary structures are effective-dated: a raise is a NEW row on a later date,
 * not an edit to the old one, so PF and ESI already computed for earlier months
 * stay as they were.
 */
function SalaryPanel({ entityId, employee, onClose }: {
  entityId: string | null; employee: Employee; onClose: () => void;
}) {
  const [rows, setRows] = useState<SalaryRow[]>([]);
  const [f, setF] = useState({ effectiveFrom: '', basic: '', da: '', hra: '', specialAllowance: '', otherAllowance: '' });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!entityId) return;
    adminFetchJson<{ items: SalaryRow[] }>(`/api/masters/employees/${employee.id}/salary`, { query: { entityId } })
      .then((d) => setRows(d.items ?? [])).catch(() => setRows([]));
  }, [entityId, employee.id]);
  useEffect(load, [load]);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await mutate(`employees/${employee.id}/salary`, 'POST', { entityId, ...f });
      setF({ effectiveFrom: '', basic: '', da: '', hra: '', specialAllowance: '', otherAllowance: '' });
      toast.success('Salary structure saved'); load();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  const money = (v: string) => `₹${Number(v).toLocaleString('en-IN')}`;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/40 p-6">
      <div className="w-full max-w-4xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-ink-100 px-6 py-5">
          <div>
            <h3 className="text-base font-semibold text-ink-900">Salary structure — {employee.name}</h3>
            <p className="mt-1 max-w-2xl text-sm text-ink-500">
              PF's ceiling applies to basic + DA; ESI's applies to gross. Both thresholds need the
              breakup, which is why a single salary figure will not do. A raise is a new row on a
              later date, so earlier months are not retro-changed.
            </p>
          </div>
          <button onClick={onClose} className="rounded-full p-1 text-ink-400 hover:bg-ink-100" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-ink-100 bg-ink-50/60 px-6 py-5">
          <FormError msg={err} />
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <Field label="Effective from"><TextInput type="date" value={f.effectiveFrom} onChange={set('effectiveFrom')} /></Field>
            <Field label="Basic"><TextInput value={f.basic} onChange={set('basic')} mono /></Field>
            <Field label="DA"><TextInput value={f.da} onChange={set('da')} mono /></Field>
            <Field label="HRA"><TextInput value={f.hra} onChange={set('hra')} mono /></Field>
            <Field label="Special allowance"><TextInput value={f.specialAllowance} onChange={set('specialAllowance')} mono /></Field>
            <Field label="Other allowance"><TextInput value={f.otherAllowance} onChange={set('otherAllowance')} mono /></Field>
          </div>
          <div className="mt-5"><PrimaryBtn onClick={submit} busy={busy}>Save structure</PrimaryBtn></div>
        </div>

        {rows.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-ink-500">No salary structure recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <Th>Effective from</Th><Th right>Basic</Th><Th right>DA</Th><Th right>HRA</Th>
                  <Th right>Special</Th><Th right>Other</Th><Th right>Gross</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {rows.map((r, i) => (
                  <tr key={r.id} className={i === 0 ? 'bg-ok-50/30' : ''}>
                    <Td>
                      {String(r.effectiveFrom).slice(0, 10)}
                      {i === 0 && <span className="ml-2"><Pill tone="ok">current</Pill></span>}
                    </Td>
                    <Td right mono>{money(r.basic)}</Td>
                    <Td right mono>{money(r.da)}</Td>
                    <Td right mono>{money(r.hra)}</Td>
                    <Td right mono>{money(r.specialAllowance)}</Td>
                    <Td right mono>{money(r.otherAllowance)}</Td>
                    <Td right mono>{money(r.grossMonthly)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
